import React, { useRef, useState } from 'react'
import { Button } from '@/common/ui/Form/Button'
import { MarkdownContent } from '@/common/ui/MarkdownContent'

export interface ExpandableTextProps {
    text: string
    wordLimit?: number
    showCharacterCount?: boolean
    className?: string
    markdown?: boolean
    defaultExpanded?: boolean
}

export const ExpandableText: React.FC<ExpandableTextProps> = ({
    text,
    wordLimit = 30,
    showCharacterCount = false,
    className = '',
    markdown = false,
    defaultExpanded = false,
}) => {
    const [isExpanded, setIsExpanded] = useState(defaultExpanded)
    const footerRef = useRef<HTMLDivElement | null>(null)

    const handleToggle = () => {
        setIsExpanded(!isExpanded)
    }

    const handleFooterToggle = () => {
        const shouldCollapse = isExpanded
        setIsExpanded(!isExpanded)

        if (!shouldCollapse) {
            return
        }

        requestAnimationFrame(() => {
            footerRef.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'end',
            })
        })
    }

    // Функция для подсчета символов
    const countCharacters = (text: string): number => {
        return text.replace(/\s+/g, ' ').trim().length
    }

    // Функция для сокращения текста до N слов
    const truncateText = (text: string, limit: number): string => {
        const words = text.trim().split(/\s+/)
        if (words.length <= limit) {
            return text
        }
        return words.slice(0, limit).join(' ')
    }

    const needsToggle = text.trim().split(/\s+/).length > wordLimit
    const displayText = isExpanded ? text : truncateText(text.replace(/\n/g, ' '), wordLimit)
    const characterCount = countCharacters(text)

    const renderContent = () => {
        if (!isExpanded && needsToggle) {
            return (
                <p>
                    {displayText}
                    <Button
                        type='button'
                        variant='ghost'
                        emphasis='accent'
                        size='sm'
                        onClick={handleToggle}
                        className='inline text-sm align-baseline p-0!'
                        aria-label='Показать полностью'
                    >
                        …
                    </Button>
                </p>
            )
        }

        if (markdown) {
            return <MarkdownContent content={text} />
        }

        return <p>{isExpanded ? text : displayText}</p>
    }

    const showFooter = needsToggle || showCharacterCount
    const showToggleButton = needsToggle

    return (
        <div className={'w-full ' + className}>
            <div className='font-normal leading-[22px] text-sm text-[var(--pixorion-gray-2)] w-full'>
                {renderContent()}
            </div>
            {showFooter && (
                <div
                    ref={footerRef}
                    className={`flex items-center ${showCharacterCount ? 'justify-between' : 'justify-end'} w-full ${showCharacterCount ? 'mt-4' : ''}`}
                >
                    {showCharacterCount && (
                        <p className='font-light leading-[22px] lowercase text-xs text-[var(--pixorion-gray-2)] text-nowrap'>
                            {characterCount} символов
                        </p>
                    )}
                    {showToggleButton && (
                        <Button type='button' variant='ghost' emphasis='accent' onClick={handleFooterToggle}>
                            {isExpanded ? 'Скрыть' : 'Показать полностью'}
                        </Button>
                    )}
                </div>
            )}
        </div>
    )
}
